import {
  VantifyProxyRequest,
  VantifyProxyResponse,
  VantifyUploadRequest,
  VantifyUploadResponse,
  VantifyApiError,
} from './types';
import {
  VANTIFY_HEADERS,
  VANTIFY_PROXY_ENDPOINT,
  VANTIFY_UPLOAD_ENDPOINT,
  VANTIFY_DEFAULT_HEADERS,
} from './endpoints';

/**
 * Build Vantify headers from auth values
 * Only adds headers that have values
 */
function buildVantifyAuthHeaders(auth: {
  channelId: string;
  username?: string;
  password?: string;
  baseUrl?: string;
  token: string;
}): Record<string, string> {
  const headers: Record<string, string> = {};

  if (auth.channelId)
    headers[VANTIFY_HEADERS.PRIMARY_CHANNEL_ID] = auth.channelId;

  if (auth.username) headers[VANTIFY_HEADERS.VANTIFY_USERNAME] = auth.username;

  if (auth.password) headers[VANTIFY_HEADERS.VANTIFY_PASSWORD] = auth.password;

  if (auth.baseUrl) headers[VANTIFY_HEADERS.VANTIFY_BASE_URL] = auth.baseUrl;

  if (auth.token)
    headers[VANTIFY_HEADERS.AUTHORIZATION] = auth.token;

  return headers;
}

/**
 * Safely parses JSON response, handling empty responses gracefully
 * Reads response as text first to avoid JSON parsing errors on empty bodies
 */
async function parseJsonResponse(response: Response): Promise<any> {
  const text = await response.text();

  if (!text || text?.trim()?.length === 0) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Make an API request to Vantify
 * @param request - The API request configuration
 */
export async function vantifyRequest<T = any, TBody = any>(
  request: VantifyProxyRequest<TBody>
): Promise<VantifyProxyResponse<T>> {
  let url = `${VANTIFY_PROXY_ENDPOINT}/${request.pathname}`;

  if (request.query && Object.keys(request.query).length > 0) {
    const params = new URLSearchParams();

    Object.entries(request.query).forEach(([key, value]) => {
      params.append(key, String(value));
    });

    url += `?${params.toString()}`;
  }

  const headers: Record<string, string> = {
    ...VANTIFY_DEFAULT_HEADERS,
  };

  if (request.auth) {
    const authHeaders = buildVantifyAuthHeaders(request.auth);

    Object.assign(headers, authHeaders);
  }

  try {
    const response = await fetch(url, {
      method: request.method,
      headers,
      body: request.body ? JSON.stringify(request.body) : undefined,
    });

    const responseData = await parseJsonResponse(response);

    if (response.status >= 300) {
      throw createHttpError(response, responseData, 'Vantify API request failed');
    }

    return {
      status: response.status,
      statusText: response.statusText,
      data: responseData?.data ?? null,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'error' in error) {
      throw error;
    }

    throw handleError(error, 'Vantify API request failed');
  }
}

/**
 * Upload a file to Vantify
 * @param request - The upload request configuration
 */
export async function vantifyUploadFile<T = any>(
  request: VantifyUploadRequest
): Promise<VantifyUploadResponse<T>> {
  const url = `${VANTIFY_UPLOAD_ENDPOINT}?filename=${encodeURIComponent(request.filename)}&type=${encodeURIComponent(request.contentType)}`;

  const headers: Record<string, string> = {
    Accept: VANTIFY_DEFAULT_HEADERS.Accept,
    'Content-Type': request.contentType,
  };

  if (request.auth) {
    const authHeaders = buildVantifyAuthHeaders(request.auth);

    Object.assign(headers, authHeaders);
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: request.file,
    });

    const responseData = await parseJsonResponse(response);

    if (response.status >= 300) {
      throw createHttpError(response, responseData, 'File upload failed');
    }

    return {
      status: response.status,
      statusText: response.statusText,
      data: responseData,
    };
  } catch (error) {
    if (error && typeof error === 'object' && 'error' in error) {
      throw error;
    }

    throw handleError(error, 'File upload failed');
  }
}

/**
 * Creates a VantifyApiError for HTTP error responses
 */
function createHttpError(
  response: Response,
  responseData: any,
  message: string
): VantifyApiError {
  const errorMessage =
    responseData?.message || responseData?.error || message;

  return {
    error: 'Request Error',
    message: errorMessage,
    details: {
      status: response.status,
      statusText: response.statusText,
      responseData,
    },
  };
}

/**
 * Handles errors and creates consistent error objects
 */
function handleError(error: any, message: string): VantifyApiError {
  const errorMessage =
    error?.message ?? (error != null ? String(error) : message);

  if (error instanceof Error) {
    return {
      error: 'Request Error',
      message: errorMessage,
      details: error.stack,
    };
  }

  return {
    error: 'Unknown Error',
    message: errorMessage,
  };
}
